import { useState, useEffect } from "react";
import { loadLeaderboardData } from "../utils/dataLoader";
import modelMetadata from "../data/model_metadata.json";

export function ModelPerformance({ theme }) {
	const [selectedModel, setSelectedModel] = useState("");
	const [performanceData, setPerformanceData] = useState(null);
	const [loading, setLoading] = useState(false);

	const tasks = [
		{ name: "Spec-to-RTL", benchmark: "All", simulator: "Icarus Verilog" },
		{ name: "Code Completion", benchmark: "All", simulator: "Icarus Verilog" },
		{ name: "Rule Completion", benchmark: "RuC", simulator: "YoSys EQV" },
		{ name: "Module Completion", benchmark: "NotSoTiny", simulator: "YoSys EQV" },
		{ name: "Line Completion", benchmark: "RTL-Repo", simulator: null },
	];

	const allMetrics = [
		"Syntax (STX)",
		"Functionality",
		"Synthesis (SYN)",
		"Power",
		"Performance",
		"Area",
		"Aggregated Score",
		"Cell Coverage",
		"Exact Matching",
	];

	const modelNames = Object.keys(modelMetadata).sort();

	useEffect(() => {
		if (!selectedModel) return;
	
		async function fetchPerformance() {
			setLoading(true);
			try {
				const results = {};
				for (const task of tasks) {
					const data = await loadLeaderboardData(
						task.name,
						task.benchmark,
						task.simulator,
						{
							modelTypes: [],
							maxParams: 1500,
							includeDeprecated: true,
							searchQuery: selectedModel,
						}
					);
					
					// Filter for the selected model
					const modelRows = data.filter(row => row.modelName === selectedModel);
					
					if (modelRows.length === 0) {
						results[task.name] = null;
					} else if (modelRows.length === 1) {
						results[task.name] = modelRows[0];
					} else {
						// Multiple versions (e.g. CHAT vs FIM). Pick the one with highest functionality.
						results[task.name] = modelRows.reduce((prev, current) => {
							const prevVal = prev["Functionality"] || prev["Functionality (EQV)"] || prev["Functionality (FNC)"] || 0;
							const currVal = current["Functionality"] || current["Functionality (EQV)"] || current["Functionality (FNC)"] || 0;
							return currVal > prevVal ? current : prev;
						});
					}
				}
				setPerformanceData(results);
			} catch (error) {
				console.error("Error fetching model performance:", error);
			} finally {
				setLoading(false);
			}
		}
	
		fetchPerformance();
	}, [selectedModel]);

	const getMetricValue = (taskName, metric) => {
		const row = performanceData?.[taskName];
		if (!row) return "";
	
		// Map friendly names to actual keys in data
		let key = metric;
		if (metric === "Functionality") {
			if (taskName === "Module Completion") key = "Functionality (EQV)";
			else if (taskName === "Rule Completion") key = "Functionality";
			else key = "Functionality (FNC)";
		} else if (metric === "Syntax (STX)") {
			if (taskName === "Rule Completion") key = "Syntax";
		} else if (metric === "Exact Matching") {
			key = "EM";
		}
		
		const value = row[key];
		return typeof value === 'number' ? value.toFixed(2) : (value || "");
	};

	return (
		<div className="w-full max-w-6xl min-w-6xl border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm p-8 mt-4">
			<div className="flex flex-col gap-6 text-gray-300">
				<div className="flex flex-col gap-2 items-start">
					<p className="text-xs font-semibold text-gray-400 capitalize tracking-wide">
						Select Model
					</p>
					<select
						value={selectedModel}
						onChange={(e) => setSelectedModel(e.target.value)}
						className="block w-full max-w-sm px-3 py-2 bg-neutral-secondary-medium border border-gray-600 rounded-xl text-heading text-sm focus:ring-brand focus:border-brand shadow-xs"
					>
						<option value="">-- Choose a model --</option>
						{modelNames.map(name => (
							<option key={name} value={name}>{name}</option>
						))}
					</select>
				</div>

				{loading ? (
					<div className="text-center py-10">Loading performance data...</div>
				) : selectedModel && performanceData ? (
					<div className="overflow-x-auto">
						<table className="w-full text-left border-collapse">
							<thead className="border-b border-gray-700" style={{ backgroundColor: '#1A212B' }}>
								<tr>
									<th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Task</th>
									{allMetrics.map(metric => (
										<th key={metric} className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
											{metric}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{tasks.map(task => (
									<tr key={task.name} className="border-b border-gray-700 hover:bg-gray-800/30 transition-colors">
										<td className="px-4 py-3 text-sm font-medium text-gray-200">{task.name}</td>
										{allMetrics.map(metric => (
											<td key={metric} className="px-4 py-3 text-sm text-center text-gray-300">
												{getMetricValue(task.name, metric)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : selectedModel ? (
					<div className="text-center py-10 text-gray-500">No data found for this model.</div>
				) : (
					<div className="text-center py-10 text-gray-500">Please select a model to see its performance across all tasks.</div>
				)}
			</div>
		</div>
	);
}
